import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { THEME_BY_KEY, THEME_KEYS } from "@/lib/guide/themes";
import { GuideCtx } from "@/lib/guide/context";
import { GuideOverlay } from "@/components/guide/GuideOverlay";
import { GuideCompleteOverlay } from "@/components/guide/GuideCompleteOverlay";

const STARTED_AT_KEY = "mw_guide_started_at";
const SESSION_HIDE_KEY = "mw_guide_session_hidden";

export function GuideProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCoach = user?.role === "coach";

  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);      // theme key
  const [stepIndex, setStepIndex] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [sessionHidden, setSessionHidden] = useState(false);

  const doneRef = useRef(null);          // previous done-set for completion diffing
  const openedAtRef = useRef({});        // themeKey -> ts
  const autoStartedRef = useRef(false);

  const track = useCallback((event, extra = {}) => {
    if (!isCoach) return;
    api.post("/coach/guide/track", { event, ...extra }).catch(() => {});
  }, [isCoach]);

  const applyStatus = useCallback((data) => {
    setStatus(data);
    const nowDone = new Set((data.themes || []).filter((t) => t.done).map((t) => t.key));
    if (doneRef.current) {
      // fire completion events for themes that flipped to done
      for (const key of nowDone) {
        if (!doneRef.current.has(key)) {
          const openedAt = openedAtRef.current[key];
          track("guide_theme_completed", { theme: key, duration_ms: openedAt ? Date.now() - openedAt : null });
        }
      }
    }
    doneRef.current = nowDone;
    // All done → one-time celebration
    if (data.all_done && !data.completed_all_seen) {
      setShowComplete(true);
    }
  }, [track]);

  const refresh = useCallback(async () => {
    if (!isCoach) return null;
    try {
      const { data } = await api.get("/coach/guide/status");
      applyStatus(data);
      return data;
    } catch {
      return null;
    }
  }, [isCoach, applyStatus]);

  // Initial load
  useEffect(() => {
    if (!isCoach) { setStatus(null); return; }
    try { setSessionHidden(sessionStorage.getItem(SESSION_HIDE_KEY) === "1"); } catch { /* ignore */ }
    refresh();
  }, [isCoach, refresh]);

  // Re-check on navigation (debounced) — content created elsewhere flips chips.
  useEffect(() => {
    if (!isCoach) return;
    const t = setTimeout(() => { refresh(); }, 500);
    return () => clearTimeout(t);
  }, [location.pathname, isCoach, refresh]);

  const openTheme = useCallback((key) => {
    if (!THEME_BY_KEY[key]) return;
    openedAtRef.current[key] = Date.now();
    setActive(key);
    setStepIndex(0);
    track("guide_theme_opened", { theme: key });
  }, [track]);

  const closeOverlay = useCallback(() => { setActive(null); setStepIndex(0); }, []);

  // Auto-start: first login of a coach (started=false) → open Profil after 2s.
  useEffect(() => {
    if (!isCoach || !status || autoStartedRef.current) return;
    if (status.started) { autoStartedRef.current = true; return; }
    autoStartedRef.current = true;
    const firstOpen = (status.themes || []).find((t) => t.open);
    const timer = setTimeout(async () => {
      try {
        await api.post("/coach/guide/start");
        try { localStorage.setItem(STARTED_AT_KEY, String(Date.now())); } catch { /* ignore */ }
        track("guide_started");
      } catch { /* ignore */ }
      openTheme(firstOpen ? firstOpen.key : "profile");
    }, 2000);
    return () => clearTimeout(timer);
  }, [isCoach, status, track, openTheme]);

  const theme = active ? THEME_BY_KEY[active] : null;
  const steps = theme ? theme.steps : [];
  const step = theme ? steps[stepIndex] : null;

  const advance = useCallback(() => {
    if (!theme) return;
    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
    else finishTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, stepIndex, steps.length]);

  const finishTheme = useCallback(async () => {
    const key = active;
    closeOverlay();
    if (key === "discover") {
      try { await api.post("/coach/guide/discover", { done: true }); } catch { /* ignore */ }
      track("guide_theme_completed", { theme: "discover" });
    }
    await refresh();
  }, [active, closeOverlay, refresh, track]);

  const primaryAction = useCallback(async () => {
    if (!step) return;
    const kind = step.primary;
    if (kind === "navigate") {
      if (step.navigate) navigate(step.navigate);
      if (active === "discover" && step.navigate) {
        try { await api.post("/coach/guide/discover", { step: stepIndex + 1 }); } catch { /* ignore */ }
      }
      if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
      else finishTheme();
    } else if (kind === "next") {
      advance();
    } else if (kind === "finish") {
      finishTheme();
    } else if (kind === "stripe") {
      try {
        const { data } = await api.post("/coach/connect/onboard");
        const url = data?.url || data?.onboarding_url || data?.account_link_url;
        if (url) { window.location.href = url; return; }
      } catch { /* ignore */ }
      closeOverlay();
    } else if (kind === "outlook") {
      try {
        const { data } = await api.get("/integrations/outlook/start");
        const url = data?.url || data?.auth_url || data?.authorize_url;
        if (url) { window.location.href = url; return; }
      } catch { /* ignore */ }
      closeOverlay();
    }
  }, [step, active, stepIndex, steps.length, navigate, advance, finishTheme, closeOverlay]);

  const later = useCallback(() => {
    track("guide_step_later", { theme: active, step: stepIndex });
    closeOverlay();
  }, [track, active, stepIndex, closeOverlay]);

  const skipTheme = useCallback(async () => {
    const key = active;
    closeOverlay();
    try { await api.post("/coach/guide/skip", { theme: key }); } catch { /* ignore */ }
    track("guide_theme_skipped", { theme: key });
    await refresh();
  }, [active, closeOverlay, track, refresh]);

  const showBar = useCallback(async () => {
    setSessionHidden(false);
    try { sessionStorage.removeItem(SESSION_HIDE_KEY); } catch { /* ignore */ }
    try { await api.post("/coach/guide/reopen"); } catch { /* ignore */ }
    const data = await refresh();
    // open first still-open theme, or profile
    const firstOpen = (data?.themes || []).find((t) => t.open);
    if (firstOpen) openTheme(firstOpen.key);
  }, [refresh, openTheme]);

  const hideBarSession = useCallback(() => {
    setSessionHidden(true);
    try { sessionStorage.setItem(SESSION_HIDE_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const dismissComplete = useCallback(async () => {
    setShowComplete(false);
    let duration = null;
    try {
      const startedAt = Number(localStorage.getItem(STARTED_AT_KEY));
      if (startedAt) duration = Date.now() - startedAt;
    } catch { /* ignore */ }
    track("guide_completed_all", { duration_ms: duration });
    try { await api.post("/coach/guide/dismiss"); } catch { /* ignore */ }
    await refresh();
  }, [track, refresh]);

  const barVisible = useMemo(() => {
    if (!isCoach || !status) return false;
    if (status.dismissed) return false;
    if (sessionHidden) return false;
    return status.completed < status.total;
  }, [isCoach, status, sessionHidden]);

  const value = useMemo(() => ({
    isCoach, status, barVisible, active, step, stepIndex, stepCount: steps.length,
    openTheme, closeOverlay, primaryAction, later, skipTheme, showBar, hideBarSession, refresh, THEME_KEYS,
  }), [isCoach, status, barVisible, active, step, stepIndex, steps.length, openTheme, closeOverlay, primaryAction, later, skipTheme, showBar, hideBarSession, refresh]);

  return (
    <GuideCtx.Provider value={value}>
      {children}
      {isCoach && active && <GuideOverlay />}
      {isCoach && showComplete && <GuideCompleteOverlay onClose={dismissComplete} />}
    </GuideCtx.Provider>
  );
}
